/**
 * Renders a visible reCAPTCHA Enterprise checkbox challenge on any form that
 * carries a challenge container (sign-in once repeated failures have added it,
 * sign-up on every render), and blocks submission until the visitor completes
 * it.
 *
 * Google does not support self-hosting enterprise.js — it must be fetched
 * live from their CDN for the fraud-scoring model to stay current — so this
 * bundled module injects the <script> tag itself rather than the Slim view
 * emitting a raw CDN <script src>. The page's CSP sets script-src to
 * strict-dynamic, so a script this module (loaded from a nonced pack tag)
 * inserts via the DOM is trusted without needing its own nonce.
 *
 * The vendor script loads asynchronously, so rendering is retried for a few
 * seconds rather than assumed ready immediately. If it never loads (CDN
 * outage, network block), the guard gives up and lets the form submit
 * unprotected rather than trapping the visitor client-side — the server
 * still refuses the submission: on sign-in the missing token reads as an
 * ordinary invalid-credentials failure counted toward the same lockout, and on
 * sign-up it rejects the registration outright. Both are intentional
 * fail-secure tradeoffs, not bypasses — an outage is ridden out by turning the
 * flag off.
 *
 * No-ops on pages without a challenge container.
 */
const ENTERPRISE_SCRIPT_SRC = 'https://www.google.com/recaptcha/enterprise.js?render=explicit';
const READY_POLL_INTERVAL_MS = 200;
const READY_POLL_TIMEOUT_MS = 8000;

const loadEnterpriseScript = () => {
  const alreadyLoading = [...document.scripts].some((it) => it.src === ENTERPRISE_SCRIPT_SRC);
  if (alreadyLoading) return;

  const script = document.createElement('script');
  script.src = ENTERPRISE_SCRIPT_SRC;
  document.head.append(script);
};

const pollForEnterprise = (onReady, onTimeout, elapsedMs = 0) => {
  const enterprise = globalThis.grecaptcha?.enterprise;
  if (enterprise) {
    enterprise.ready(() => onReady(enterprise));
    return;
  }

  if (elapsedMs >= READY_POLL_TIMEOUT_MS) {
    onTimeout();
    return;
  }

  setTimeout(
    () => pollForEnterprise(onReady, onTimeout, elapsedMs + READY_POLL_INTERVAL_MS),
    READY_POLL_INTERVAL_MS,
  );
};

const guardContainer = (container) => {
  const { recaptchaSiteKey: siteKey } = container.dataset;
  const form = container.closest('form');
  const tokenField = form?.querySelector('input[name="recaptcha_token"]');
  if (!siteKey || !form || !tokenField) return;

  let widgetAvailable = false;
  const setToken = (token) => {
    tokenField.value = token || '';
  };

  const renderWidget = (enterprise) => {
    widgetAvailable = true;
    enterprise.render(container, {
      sitekey: siteKey,
      callback: setToken,
      'expired-callback': () => setToken(''),
      'error-callback': () => setToken(''),
    });
  };

  loadEnterpriseScript();
  pollForEnterprise(renderWidget, () => {});

  // Capture phase + stopImmediatePropagation: runs before, and suppresses,
  // initFormSubmitGuard's bubble-phase listener on the same form/event —
  // otherwise that listener disables the submit button before this one can
  // block the submission, and nothing re-enables it once the captcha is
  // solved afterward.
  form.addEventListener(
    'submit',
    (event) => {
      // The sign-up challenge lives in the profile fieldset, which stays hidden
      // while the password step is on screen — that step's own submit is an
      // in-page transition, not an account creation, so it must pass through.
      if (container.offsetParent === null) return;
      if (!widgetAvailable || tokenField.value) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      container.scrollIntoView({ block: 'center' });
    },
    { capture: true },
  );
};

export const initRecaptchaGuard = () => {
  for (const container of document.querySelectorAll('[data-recaptcha-site-key]')) {
    guardContainer(container);
  }
};
